import { CustomerPortalSessionData } from '@monetize/types/app';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { MBox, MFlex, MText } from '../../../components/Monetize';
import { CustomerPortalAuth } from './components/CustomerPortalAuth';
import { CustomerPortalFooter } from './components/CustomerPortalFooter';

interface StoredSession {
  sessionId: string;
  accessToken: string;
  accountId: string;
  billGroupId: string;
  email?: string;
}

function getStorageKey(tenantId: string, shareSecret: string): string {
  return `mn:customerPortal:${tenantId}:${shareSecret}:session`;
}

function loadStoredSession(storageKey: string): StoredSession | null {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored) as StoredSession;
  } catch {
    return null;
  }
}

function saveStoredSession(storageKey: string, session: StoredSession): void {
  localStorage.setItem(storageKey, JSON.stringify(session));
}

interface CustomerPortalLayoutProps {
  children: (
    sessionData: CustomerPortalSessionData,
    onLogout: () => void,
  ) => ReactNode;
}

export const CustomerPortalLayout = ({
  children,
}: CustomerPortalLayoutProps) => {
  const pathParams = useParams();
  const [searchParams] = useSearchParams();
  const tenantId = pathParams.tenantId as string | undefined;
  const shareSecret = searchParams.get('token') || undefined;

  const storageKey = useMemo(() => {
    if (!tenantId || !shareSecret) {
      return undefined;
    }
    return getStorageKey(tenantId, shareSecret);
  }, [shareSecret, tenantId]);

  const [session, setSession] = useState<StoredSession | null>(() => {
    if (!storageKey) {
      return null;
    }
    return loadStoredSession(storageKey);
  });

  const handleAuthSuccess = useCallback(
    (
      sessionId: string,
      accessToken: string,
      accountId: string,
      billGroupId: string,
      email: string,
    ) => {
      const newSession: StoredSession = {
        sessionId,
        accessToken,
        accountId,
        billGroupId,
        email,
      };
      if (storageKey) {
        saveStoredSession(storageKey, newSession);
      }
      setSession(newSession);
    },
    [storageKey],
  );

  const handleLogout = useCallback(() => {
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    setSession(null);
  }, [storageKey]);

  if (!tenantId || !shareSecret) {
    return (
      <MBox minH="100vh" display="flex" flexDirection="column">
        <MFlex
          flex={1}
          alignItems="center"
          justifyContent="center"
          px={4}
          py={8}
        >
          <MBox
            bg="white"
            borderRadius="lg"
            p={8}
            maxW="400px"
            w="100%"
            textAlign="center"
            boxShadow="md"
          >
            <MText fontSize="xl" fontWeight="600" color="gray.800" mb={2}>
              Invalid Link
            </MText>
            <MText color="gray.500" fontSize="sm" mb={4}>
              This customer portal link appears to be invalid.
            </MText>
            <MText color="gray.400" fontSize="xs">
              Please check the link you received and try again, or contact
              support for assistance.
            </MText>
          </MBox>
        </MFlex>
        <CustomerPortalFooter />
      </MBox>
    );
  }

  if (!session) {
    return (
      <CustomerPortalAuth
        tenantId={tenantId}
        shareSecret={shareSecret}
        onAuthSuccess={handleAuthSuccess}
      />
    );
  }

  return (
    <MBox bg="white" minH="100vh">
      {children(
        {
          tenantId,
          accountId: session.accountId,
          billGroupId: session.billGroupId,
          sessionId: session.sessionId,
          accessToken: session.accessToken,
          email: session.email ?? '',
        },
        handleLogout,
      )}
    </MBox>
  );
};
